"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { InternalVolumeUnavailable, useInternalVolumeAccess } from "@/lib/useInternalVolumeAccess";
import IndexBadges from "@/components/IndexBadges";

type KlingerRow = {
  symbol: string;
  sector: string;
  company: string;
  direction: "Bullish" | "Bearish";
  signal_types: string;
  zone: string;
  close: number;
  pct_from_sma50: number | null;
  pct_from_sma200: number | null;
  ko: number;
  signal_val: number;
  ko_signal_spread: number;
  bars_since_opposite: number;
  atr: number | null;
  atr_pct: number | null;
  vol_vs_avg50: number;
  avg_turnover_20d: number;
  circuit_locked: boolean;
  indices?: string[];
};

type KlingerResponse = {
  ok: boolean;
  generated_at: string;
  scanned: number;
  total: number;
  bullish_count: number;
  bearish_count: number;
  results: KlingerRow[];
};

type DirectionFilter = "all" | "Bullish" | "Bearish";
type ZoneFilter = "all" | "oversold-cross" | "overbought-cross" | "continuation-cross";

const fmt = (v: number | null | undefined, d = 2) =>
  v == null ? "—" : v.toLocaleString(undefined, { maximumFractionDigits: d });

const fmtPct = (v: number | null | undefined) =>
  v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;

const fmtKO = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toFixed(1);
};

const ZONE_LABELS: Record<string, string> = {
  "oversold-cross": "Oversold",
  "overbought-cross": "Overbought",
  "continuation-cross": "Continuation",
};

function directionStyle(dir: string) {
  const bullish = dir === "Bullish";
  return {
    display: "inline-block",
    padding: "0.2rem 0.65rem",
    borderRadius: "999px",
    fontSize: "0.72rem",
    fontWeight: 700,
    background: bullish ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
    color: bullish ? "var(--color-success)" : "var(--color-danger)",
    border: `1px solid ${bullish ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)"}`,
    whiteSpace: "nowrap" as const,
  };
}

function zonePillStyle(zone: string) {
  const isStrong = zone === "oversold-cross" || zone === "overbought-cross";
  return {
    display: "inline-block",
    padding: "0.2rem 0.6rem",
    borderRadius: "999px",
    fontSize: "0.7rem",
    fontWeight: 600,
    background: isStrong ? "rgba(251,191,36,0.15)" : "rgba(148,163,184,0.1)",
    color: isStrong ? "#fbbf24" : "rgba(148,163,184,0.75)",
    border: `1px solid ${isStrong ? "rgba(251,191,36,0.3)" : "rgba(148,163,184,0.2)"}`,
    whiteSpace: "nowrap" as const,
  };
}

const summaryCardStyle = {
  background: "rgba(15, 23, 42, 0.6)",
  border: "1px solid rgba(148, 163, 184, 0.15)",
  borderRadius: "10px",
  padding: "0.85rem 1rem",
} as const;

const filterBtnStyle = (active: boolean) => ({
  padding: "0.4rem 0.9rem",
  borderRadius: "999px",
  fontWeight: 600,
  fontSize: "0.85rem",
  cursor: "pointer",
  border: active ? "1px solid rgba(59,130,246,0.55)" : "1px solid rgba(148,163,184,0.22)",
  background: active ? "rgba(59,130,246,0.18)" : "rgba(15,23,42,0.5)",
  color: "var(--color-text)",
});

export default function KlingerScannerPage() {
  const { allowed, checked, token } = useInternalVolumeAccess();
  const [data, setData] = useState<KlingerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dirFilter, setDirFilter] = useState<DirectionFilter>("all");
  const [zoneFilter, setZoneFilter] = useState<ZoneFilter>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!checked || !allowed || !token) return;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch("/api/scanner/klinger", {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as KlingerResponse;
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load Klinger data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [allowed, checked, token]);

  const rows = useMemo(() => {
    if (!data?.results) return [];
    const term = search.trim().toUpperCase();
    return data.results.filter((r) => {
      if (term && !r.symbol.includes(term) && !r.company?.toUpperCase().includes(term)) return false;
      if (dirFilter !== "all" && r.direction !== dirFilter) return false;
      if (zoneFilter !== "all" && r.zone !== zoneFilter) return false;
      return true;
    });
  }, [data, search, dirFilter, zoneFilter]);

  const scanDate = data?.generated_at
    ? new Date(data.generated_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "N/A";

  const DIR_FILTERS: { value: DirectionFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "Bullish", label: `Bullish (${data?.bullish_count ?? "—"})` },
    { value: "Bearish", label: `Bearish (${data?.bearish_count ?? "—"})` },
  ];

  const ZONE_FILTERS: { value: ZoneFilter; label: string }[] = [
    { value: "all", label: "All Zones" },
    { value: "oversold-cross", label: "Oversold" },
    { value: "overbought-cross", label: "Overbought" },
    { value: "continuation-cross", label: "Continuation" },
  ];

  return (
    !checked ? (
      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          <p style={{ color: "var(--color-text-muted)" }}>Checking access...</p>
        </div>
      </section>
    ) : !allowed ? (
      <InternalVolumeUnavailable />
    ) : (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Klinger Oscillator Scanner</h1>
          <p className="page-subtitle">
            KVO signal-line crossovers on PSX stocks — oversold bullish crosses and overbought bearish crosses are the strongest setups.
          </p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">

          {/* Summary bar */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "0.75rem",
            marginBottom: "1.5rem",
          }}>
            {[
              { label: "Scan Date", value: scanDate },
              { label: "Scanned", value: data ? fmt(data.scanned, 0) : "—" },
              { label: "Signals", value: data ? String(data.total) : "—" },
              { label: "Bullish", value: data ? String(data.bullish_count) : "—" },
              { label: "Bearish", value: data ? String(data.bearish_count) : "—" },
              { label: "Showing", value: String(rows.length) },
            ].map((c) => (
              <div key={c.label} style={summaryCardStyle}>
                <div style={{ fontSize: "0.72rem", color: "rgba(148,163,184,0.65)", marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{c.label}</div>
                <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>{c.value}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem", alignItems: "center" }}>
            {DIR_FILTERS.map((f) => (
              <button key={f.value} type="button" onClick={() => setDirFilter(f.value)} style={filterBtnStyle(dirFilter === f.value)}>
                {f.label}
              </button>
            ))}
            <div style={{ width: "1px", height: "20px", background: "rgba(148,163,184,0.2)", margin: "0 0.25rem" }} />
            {ZONE_FILTERS.map((f) => (
              <button key={f.value} type="button" onClick={() => setZoneFilter(f.value)} style={filterBtnStyle(zoneFilter === f.value)}>
                {f.label}
              </button>
            ))}
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search symbol"
              style={{
                marginLeft: "auto",
                padding: "0.4rem 0.75rem",
                borderRadius: "8px",
                border: "1px solid rgba(148,163,184,0.3)",
                background: "rgba(15,23,42,0.5)",
                color: "var(--color-text)",
                fontSize: "0.85rem",
                width: "150px",
              }}
            />
          </div>

          {loading && (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
              Loading Klinger scan…
            </div>
          )}

          {error && (
            <div style={{ padding: "1rem", borderRadius: "12px", border: "1px solid rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.08)", color: "var(--color-danger)", marginBottom: "1rem" }}>
              {error}
            </div>
          )}

          {!loading && !error && (
            <div style={{ overflowX: "auto", borderRadius: "14px", border: "1px solid rgba(148,163,184,0.2)", background: "rgba(15,23,42,0.55)" }}>
              <table className="data-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {[
                      ["Symbol", "left"],
                      ["Sector", "left"],
                      ["Direction", "center"],
                      ["Zone", "center"],
                      ["Price", "right"],
                      ["vs 50 SMA", "right"],
                      ["vs 200 SMA", "right"],
                      ["KO", "right"],
                      ["Signal", "right"],
                      ["Spread", "right"],
                      ["Freshness", "right"],
                      ["ATR %", "right"],
                      ["Vol / Avg", "right"],
                    ].map(([h, align]) => (
                      <th key={h as string} style={{
                        padding: "0.85rem",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        color: "var(--color-text-muted)",
                        textAlign: align as "left" | "right" | "center",
                        whiteSpace: "nowrap",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={13} style={{ textAlign: "center", padding: "2rem", color: "var(--color-text-muted)" }}>
                        No signals match the selected filters.
                      </td>
                    </tr>
                  ) : rows.map((row, idx) => (
                    <tr
                      key={row.symbol}
                      style={{
                        background: row.direction === "Bullish"
                          ? idx % 2 === 0 ? "rgba(34,197,94,0.04)" : "rgba(34,197,94,0.02)"
                          : idx % 2 === 0 ? "rgba(239,68,68,0.04)" : "rgba(239,68,68,0.02)",
                      }}
                    >
                      <td style={{ padding: "0.7rem 0.85rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                        <Link href={`/stock/${row.symbol}`} style={{ color: "var(--color-text)", textDecoration: "none" }}>
                          {row.symbol}
                        </Link>
                        {row.circuit_locked && (
                          <span title="Circuit locked" style={{ marginLeft: "0.35rem", fontSize: "0.65rem", color: "#f59e0b" }}>⚡</span>
                        )}
                        <IndexBadges indices={row.indices ?? []} style={{ marginLeft: 5, verticalAlign: "middle" }} />
                        <div style={{ fontSize: "0.72rem", color: "rgba(148,163,184,0.55)", fontWeight: 400, marginTop: "0.1rem" }}>
                          {row.signal_types}
                        </div>
                      </td>
                      <td style={{ padding: "0.7rem 0.85rem", color: "var(--color-text-muted)", fontSize: "0.82rem" }}>
                        {row.sector || "—"}
                      </td>
                      <td style={{ padding: "0.7rem 0.85rem", textAlign: "center" }}>
                        <span style={directionStyle(row.direction)}>{row.direction}</span>
                      </td>
                      <td style={{ padding: "0.7rem 0.85rem", textAlign: "center" }}>
                        <span style={zonePillStyle(row.zone)}>{ZONE_LABELS[row.zone] ?? row.zone}</span>
                      </td>
                      <td style={{ padding: "0.7rem 0.85rem", textAlign: "right", fontWeight: 600 }}>
                        Rs. {fmt(row.close)}
                      </td>
                      <td style={{
                        padding: "0.7rem 0.85rem", textAlign: "right", fontWeight: 600,
                        color: row.pct_from_sma50 == null ? "inherit" : row.pct_from_sma50 >= 0 ? "var(--color-success)" : "var(--color-danger)",
                      }}>
                        {fmtPct(row.pct_from_sma50)}
                      </td>
                      <td style={{
                        padding: "0.7rem 0.85rem", textAlign: "right", fontWeight: 600,
                        color: row.pct_from_sma200 == null ? "inherit" : row.pct_from_sma200 >= 0 ? "var(--color-success)" : "var(--color-danger)",
                      }}>
                        {fmtPct(row.pct_from_sma200)}
                      </td>
                      <td style={{ padding: "0.7rem 0.85rem", textAlign: "right", fontSize: "0.85rem", color: row.ko >= 0 ? "var(--color-success)" : "var(--color-danger)" }}>
                        {fmtKO(row.ko)}
                      </td>
                      <td style={{ padding: "0.7rem 0.85rem", textAlign: "right", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                        {fmtKO(row.signal_val)}
                      </td>
                      <td style={{ padding: "0.7rem 0.85rem", textAlign: "right", fontWeight: 700, color: row.ko_signal_spread >= 0 ? "var(--color-success)" : "var(--color-danger)" }}>
                        {fmtKO(row.ko_signal_spread)}
                      </td>
                      <td style={{ padding: "0.7rem 0.85rem", textAlign: "right", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                        {row.bars_since_opposite >= 0 ? `${row.bars_since_opposite}d` : "—"}
                      </td>
                      <td style={{ padding: "0.7rem 0.85rem", textAlign: "right", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                        {fmtPct(row.atr_pct)}
                      </td>
                      <td style={{
                        padding: "0.7rem 0.85rem", textAlign: "right", fontWeight: 600,
                        color: row.vol_vs_avg50 >= 2 ? "#f59e0b" : row.vol_vs_avg50 >= 1.2 ? "var(--color-success)" : "var(--color-text-muted)",
                      }}>
                        {fmt(row.vol_vs_avg50, 1)}x
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          {!loading && !error && data && (
            <div style={{ marginTop: "1.25rem", fontSize: "0.78rem", color: "rgba(148,163,184,0.55)", display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
              <span>⚡ Circuit-locked on signal day — cross may extend next session</span>
              <span>Oversold/Overbought crosses are higher-quality setups</span>
              <span>Trend filter ON: bullish signals require price &gt; 200 SMA</span>
            </div>
          )}
        </div>
      </section>
    </>
    )
  );
}
